import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { styled as styledMUI } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { TOAST_CONFIG, API_BASE } from '../../shared/Constants';
import { useIntegrations, IntegrationConnection } from '../../shared/context/IntegrationsContext';
// Original Slack mark: https://a.slack-edge.com/1f40066/marketing/img/nav/logo.svg
import slackLogo from '../../static/images/slack.svg';

// Escopo de escrita do Slack: sufixo ":write" (chat:write, channels:write...).
const isWriteScope = (scope: string) => scope.endsWith(':write');

export interface IntegrationsModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

function IntegrationsModal({ openModal, setOpenModal }: IntegrationsModalProps) {
  const { connections, loading, fetchConnections, connectSlack, disconnect } = useIntegrations();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [expandedConnection, setExpandedConnection] = useState<string | null>(null);

  useEffect(() => {
    if (!openModal) setExpandedConnection(null);
  }, [openModal]);

  useEffect(() => {
    if (openModal) {
      fetchConnections();
    }
  }, [openModal, fetchConnections]);

  // A janela do OAuth avisa a aba do chat (postMessage) quando o callback termina.
  const onOAuthMessage = useCallback((event: MessageEvent) => {
    const apiOrigin = API_BASE ? new URL(API_BASE).origin : window.location.origin;
    if (event.origin !== apiOrigin) return;
    const data = event.data;
    if (!data || data.type !== 'maritaca-integration' || data.provider !== 'slack') return;
    if (data.status === 'connected') {
      toast.success('Slack conectado.', TOAST_CONFIG);
    } else {
      toast.error(data.message || 'Não foi possível conectar o Slack.', TOAST_CONFIG);
    }
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    if (!openModal) return;
    window.addEventListener('message', onOAuthMessage);
    return () => window.removeEventListener('message', onOAuthMessage);
  }, [openModal, onOAuthMessage]);

  const slackConnections = connections.filter(c => c.provider === 'slack');

  const handleConnectSlack = async () => {
    setConnecting(true);
    const result = await connectSlack();
    setConnecting(false);
    if (result.success && result.authorize_url) {
      window.open(result.authorize_url, '_blank', 'width=600,height=750');
    } else {
      toast.error(result.message || 'Erro ao conectar o Slack.', TOAST_CONFIG);
    }
  };

  const handleDisconnect = async (connection: IntegrationConnection) => {
    const label = connection.workspace_name ? `Slack (${connection.workspace_name})` : 'Slack';
    if (!window.confirm(`Deseja desconectar o ${label}?`)) return;
    setDisconnecting(connection.connection_id);
    const result = await disconnect(connection.connection_id);
    setDisconnecting(null);
    if (result.success) {
      toast.success(result.message, TOAST_CONFIG);
    } else {
      toast.error(result.message, TOAST_CONFIG);
    }
  };

  return (
    <StyledModal
      open={openModal}
      onClose={() => setOpenModal(false)}
      BackdropProps={{ onClick: (event) => event.stopPropagation() }}
    >
      <ModalContainer role="dialog" aria-modal="true" aria-labelledby="integrations-title">
        <Header>
          <Title id="integrations-title">Conectores</Title>
          <IconButton onClick={() => setOpenModal(false)} aria-label="Fechar conectores">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Header>

        <Content>
          {loading ? (
            <LoadingContainer role="status">
              <CircularProgress size={20} color="inherit" />
              <span>Carregando...</span>
            </LoadingContainer>
          ) : (
            <ConnectorList aria-label="Conectores disponíveis">
              {slackConnections.length === 0 && (
                <ConnectorItem>
                  <ConnectorRow>
                    <ProviderIcon src={slackLogo} alt="" width={32} height={32} />
                    <ConnectorIdentity>
                      <ConnectorName>Slack</ConnectorName>
                      <SecondaryText>Mensagens e canais</SecondaryText>
                    </ConnectorIdentity>
                    <ActionButton onClick={handleConnectSlack} disabled={connecting}>
                      {connecting && <CircularProgress size={14} color="inherit" />}
                      {connecting ? 'Conectando...' : 'Conectar'}
                    </ActionButton>
                  </ConnectorRow>
                </ConnectorItem>
              )}

              {slackConnections.map(connection => {
                const expanded = expandedConnection === connection.connection_id;
                const active = connection.status === 'active';
                const needsAttention = !active || Boolean(connection.last_error);
                const workspace = connection.workspace_name || connection.account_label;
                const label = workspace ? `Slack (${workspace})` : 'Slack';
                const detailsId = `integration-details-${connection.connection_id}`;

                return (
                  <ConnectorItem key={connection.connection_id}>
                    <ConnectorRow>
                      <ProviderIcon src={slackLogo} alt="" width={32} height={32} />
                      <ConnectorIdentity>
                        <ConnectorName>Slack</ConnectorName>
                        {workspace && <SecondaryText title={workspace}>{workspace}</SecondaryText>}
                      </ConnectorIdentity>
                      <ConnectionActions>
                        {active ? (
                          <Status $warning={needsAttention} title={needsAttention ? 'Verificar conexão' : 'Conectado'}>
                            {needsAttention ? <ErrorOutlineIcon fontSize="inherit" /> : <CheckIcon fontSize="inherit" />}
                            <StatusText>{needsAttention ? 'Verificar conexão' : 'Conectado'}</StatusText>
                          </Status>
                        ) : (
                          <ActionButton onClick={handleConnectSlack} disabled={connecting}>
                            {connecting && <CircularProgress size={14} color="inherit" />}
                            {connecting ? 'Conectando...' : 'Reconectar'}
                          </ActionButton>
                        )}
                        <DetailsButton
                          onClick={() => setExpandedConnection(expanded ? null : connection.connection_id)}
                          aria-label={`Detalhes de ${label}`}
                          aria-expanded={expanded}
                          aria-controls={detailsId}
                          $expanded={expanded}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </DetailsButton>
                      </ConnectionActions>
                    </ConnectorRow>

                    <ConnectionDetails id={detailsId} hidden={!expanded}>
                      <DetailList>
                        {connection.account_label && (
                          <Detail><dt>Conta</dt><dd>{connection.account_label}</dd></Detail>
                        )}
                        <Detail>
                          <dt>Acesso</dt>
                          <dd>{connection.scopes.length === 0 ? 'Não informado' : connection.scopes.some(isWriteScope) ? 'Leitura e escrita' : 'Somente leitura'}</dd>
                        </Detail>
                        {connection.connected_at && (
                          <Detail>
                            <dt>Conectado em</dt>
                            <dd>{new Date(connection.connected_at).toLocaleDateString('pt-BR')}</dd>
                          </Detail>
                        )}
                      </DetailList>
                      {connection.last_error && <ErrorDetail>{connection.last_error}</ErrorDetail>}
                      <DisconnectButton
                        onClick={() => handleDisconnect(connection)}
                        disabled={disconnecting === connection.connection_id}
                      >
                        {disconnecting === connection.connection_id && <CircularProgress size={14} color="inherit" />}
                        {disconnecting === connection.connection_id ? 'Desconectando...' : 'Desconectar'}
                      </DisconnectButton>
                    </ConnectionDetails>
                  </ConnectorItem>
                );
              })}
            </ConnectorList>
          )}
        </Content>
      </ModalContainer>
    </StyledModal>
  );
}

export default IntegrationsModal;

const StyledModal = styledMUI(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  font-family: var(--font-secondary);
  background: ${props => props.theme.modal_background};
  color: ${props => props.theme.font_color};
  border: 1px solid ${props => props.theme.modal_border};
  width: min(calc(100vw - 32px), 560px);
  max-height: 90dvh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  outline: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  button:focus-visible {
    outline: 2px solid ${props => props.theme.font_color};
    outline-offset: 3px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  font-family: var(--font-primary);
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${props => props.theme.text_secondary};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme.hover_overlay_light};
    color: ${props => props.theme.font_color};
  }
`;

const Content = styled.div`
  padding: 0 24px 12px;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 0 16px 8px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 88px;
  font-size: 0.875rem;
  color: ${props => props.theme.text_secondary};
`;

const ConnectorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ConnectorItem = styled.li`
  border-top: 1px solid ${props => props.theme.modal_border};
`;

const ConnectorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 88px;
  padding: 18px 0;

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const ProviderIcon = styled.img`
  flex-shrink: 0;
  display: block;
`;

const ConnectorIdentity = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const ConnectorName = styled.span`
  font-size: 0.9375rem;
  font-weight: 500;
`;

const SecondaryText = styled.span`
  font-size: 0.8125rem;
  color: ${props => props.theme.text_secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ConnectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const Status = styled.span<{ $warning: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8125rem;
  color: ${props => props.$warning
    ? (props.theme.mode === 'dark' ? '#fbbf24' : '#92400e')
    : (props.theme.mode === 'dark' ? '#86d9ac' : '#26734d')};

  svg {
    font-size: 18px;
  }
`;

const StatusText = styled.span`
  @media (max-width: 480px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
`;

const DetailsButton = styled(IconButton)<{ $expanded: boolean }>`
  svg {
    transform: rotate(${props => props.$expanded ? '180deg' : '0deg'});
  }
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.modal_border};
  border-radius: 8px;
  background: transparent;
  color: ${props => props.theme.font_color};
  font-family: inherit;
  font-weight: 500;
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${props => props.theme.hover_overlay_light};
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const ConnectionDetails = styled.div`
  padding: 0 0 18px 46px;

  @media (max-width: 480px) {
    padding-left: 0;
  }
`;

const DetailList = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 16px;
  font-size: 0.8125rem;
  line-height: 1.5;
`;

const Detail = styled.div`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 12px;

  dt {
    color: ${props => props.theme.text_secondary};
  }

  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;

const ErrorDetail = styled.p`
  margin: 0 0 16px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${props => props.theme.mode === 'dark' ? '#fbbf24' : '#92400e'};
  overflow-wrap: anywhere;
`;

const DisconnectButton = styled(ActionButton)`
  color: ${props => props.theme.error_message_text};
`;
